<template>
  <Dashboard>
    <!-- Loading -->
    <div v-if="loading" class="flex w-full flex-col gap-4">
      <CoachPageSkeleton />
    </div>

    <!-- Empty / Error / Upsell states -->
    <div v-else-if="showEmptyState" class="flex flex-col gap-4">
      <div class="flex flex-col gap-4">
        <h1 class="text-4xl font-medium text-text-primary">{{ $t('coach.page.title') }}</h1>
        <p class="text-text-secondary text-sm font-light w-2/3">
          {{ $t('coach.page.tagline') }}
        </p>
        <CoachEmptyState
          :error="profileError"
          :user="user"
          :userSettings="userSettings"
          :showMatches="showMatches"
        />
      </div>
    </div>

    <!-- Main content -->
    <div v-else class="flex flex-col gap-16">
      <!-- Header + Filters -->
      <div class="flex gap-4">
        <div class="flex flex-col gap-4 flex-1">
          <h1 class="text-4xl font-medium text-text-primary">{{ $t('coach.page.title') }}</h1>
          <p class="text-text-secondary text-sm font-light w-2/3">
            {{ $t('coach.page.tagline') }}
          </p>
        </div>
        <CoachFilters
          :platform="selectedPlatform"
          :matchType="selectedMatchType"
          :map="selectedMap"
          :maps="playerMaps"
          @update:platform="onPlatformChange"
          @update:matchType="onMatchTypeChange"
          @update:map="onMapChange"
        />
      </div>

      <!-- No matches for current filters -->
      <template v-if="hasNoFilteredMatches">
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          <div
            :class="dailyRoutines?.length > 0 ? 'md:col-span-1 xl:col-span-2' : 'md:col-span-2 xl:col-span-3'"
            class="flex flex-col items-center justify-center gap-4 py-20 px-6 bg-gray-900 rounded-lg"
          >
            <div class="min-w-[48px] w-[48px] h-[48px] rounded-full flex justify-center items-center bg-gray-800">
              <img class="w-6 h-6" src="../../assets/icons/stats-icon.svg" />
            </div>
            <h1 class="text-text-primary font-medium text-lg">{{ $t('coach.page.no_filtered_matches_title') }}</h1>
            <i18n-t scope="global" keypath="coach.page.no_filtered_matches_body" tag="h2" class="text-text-secondary text-sm text-center leading-5">
              <template #filter><span class="text-text-primary">{{ noMatchesFilterLabel }}</span></template>
              <template #br><br /></template>
            </i18n-t>
          </div>

          <!-- Routines aren't filtered, so one the player already has survives a
               filter that matches nothing. -->
          <Routine
            v-if="dailyRoutines?.length > 0"
            :routines="dailyRoutines"
            :user="user"
            @launchRoutine="launchRoutine"
            @refreshRoutines="getDailyRoutines"
            class="h-auto flex col-span-1"
          />
        </div>
      </template>

      <template v-else>
        <CoachProfileFeedbackCard
          v-if="coachFeedbackEnabled"
          class="-mt-8"
          :accessLevel="effectiveAccessLevel(user)"
          :map="selectedMap"
          :narrowedByOtherFilters="!!(selectedPlatform || selectedMatchType)"
        />

        <!-- Overview (Highlights + Quick Stats + Routine) -->
        <CoachOverview
          :orderedProgress="orderedProgress"
          :extraStats="extraStats"
          :matchHistory="matchHistory"
          :profileStats="profileStats"
          :dailyRoutines="dailyRoutines"
          :user="user"
          @launchRoutine="launchRoutine"
          @refreshRoutines="getDailyRoutines"
        />

        <!-- Weaknesses -->
        <div class="flex flex-col gap-4">
          <div class="flex gap-2 items-center">
            <h1 class="text-text-primary text-2xl font-semibold flex">{{ $t('coach.shared.weaknesses') }}</h1>
            <span class="w-6 h-6 flex items-center justify-center text-danger-400 bg-danger-900 rounded-md">
              {{ Math.min(lowestStats.length, 4) }}
            </span>
          </div>
          <WeaknessessSection
            :lowestStats="lowestStats"
            :rankComparisonUnavailable="rankComparisonUnavailable"
            :selectedWeaknessStat="selectedWeaknessStat"
            :updateSelectedWeaknessStat="(s) => selectedWeaknessStat = s"
            :user="user"
            :userMatchHistory="matchHistory"
            :toTitleCase="toTitleCase"
            :formatStatString="formatStatString"
            :formatDataString="formatDataString"
            :options="chartOptions"
            :getChartData="getChartData"
            :launchMod="launchMod"
          />
        </div>

        <!-- Strengths -->
        <div class="flex flex-col gap-4">
          <div class="flex gap-2 items-center">
            <h1 class="text-text-primary text-2xl font-semibold flex">{{ $t('coach.shared.strengths') }}</h1>
            <span class="w-6 h-6 flex items-center justify-center text-success-400 bg-success-900 rounded-md">
              {{ Math.min(positiveStats.length, 3) }}
            </span>
          </div>
          <div v-if="positiveStats?.length > 0" class="grid grid-cols-1 lg:grid-cols-3 w-full gap-6">
            <StatBlock
              v-for="stat in positiveStats.slice(0, 3)"
              :key="stat.title"
              :stat="stat"
              :style="'strength'"
              :show-improve-button="false"
              :is-match="false"
            />
          </div>
        </div>

        <!-- Maps -->
        <CoachMaps
          :maps="mapStats?.maps"
          :statlessMaps="statlessMaps"
          :bestMap="bestMap"
          :worstMap="worstMap"
        />

        <!-- All Statistics -->
        <CoachStatistics
          :stats="filteredPlayerStats || {}"
          :selectedWeaponType="selectedWeaponType"
          :selectedWeapon="selectedWeapon"
          :availableWeaponTypes="availableWeaponTypes"
          :availableWeapons="availableWeapons"
          @update:weaponType="(v) => { selectedWeaponType = v; selectedWeapon = ''; }"
          @update:weapon="(v) => selectedWeapon = v"
        />

        <!-- Match History -->
        <CoachMatchHistory
          :matches="matchHistory"
          :loading="matchHistoryLoading"
          :recentStats="recentStats"
          :currentPage="currentPage"
          :totalPages="totalPageCount"
          @changePage="onPageChange"
        />
      </template>
    </div>
  </Dashboard>

  <StartServerModal
    :server="serverInfo"
    :show="showServerModal"
    :showMapSelector="showMapSelector"
    @close="showServerModal = false"
  />
</template>

<script setup>
import {
  ref, computed, watch, onMounted, onUnmounted,
} from 'vue';
import { useStore } from 'vuex';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import axios from 'axios';

// Layout & Shared Components
import { Chart, registerables } from 'chart.js';
import { useHead } from '@unhead/vue';
import Dashboard from '../../components/layouts/DashboardLayout.vue';
import CoachPageSkeleton from '../../components/Coach/CoachPageSkeleton.vue';
import StartServerModal from '../../components/StartServerModal.vue';
import StatBlock from '../../components/Matches/filters/StatBlock.vue';
import WeaknessessSection from '../../components/Coach/WeaknessessSection.vue';

// Section Components
import Routine from '../../components/Coach/Routine.vue';
import CoachOverview from '../../components/Coach/sections/CoachOverview.vue';
import CoachMaps from '../../components/Coach/sections/CoachMaps.vue';
import CoachStatistics from '../../components/Coach/sections/CoachStatistics.vue';
import CoachMatchHistory from '../../components/Coach/sections/CoachMatchHistory.vue';
import CoachFilters from '../../components/Coach/sections/CoachFilters.vue';
import CoachEmptyState from '../../components/Coach/sections/CoachEmptyState.vue';
import CoachProfileFeedbackCard from '../../components/Coach/sections/CoachProfileFeedbackCard.vue';

// Composable & Utils
import { useCoachData } from '../../composables/useCoachData.js';
import { platformFilterDisplayLabel } from '../../utils/matchPlatform.js';
import {
  formatStatString, toTitleCase, formatDataString, formatData,
} from '../../utils/formatters';
import {
  categorizeWeapons, getAvailableWeapons, getFilteredPlayerStats, getAvailableWeaponTypes,
} from '../../utils/coach_utils';
import { compareStatToAverage, hasRankAverage } from '../../utils/stat_comparison.js';
import { createClickablePointsPlugin } from '../../utils/chartPlugins.js';
import { coachFeedbackEnabledFor, effectiveAccessLevel } from '../../utils/coach_feedback.js';

const { t } = useI18n();
useHead({ title: () => t('coach.page.head_title') });

// --- Setup ---
const router = useRouter();
const store = useStore();
const auth = useAuth();
const user = ref(auth.user());

// Filters
const selectedPlatform = ref('');
const selectedMatchType = ref('');
const selectedMap = ref(null);
const selectedWeaponType = ref('');
const selectedWeapon = ref('');
const selectedWeaknessStat = ref(null);

// Server modal
const showServerModal = ref(false);
const serverInfo = ref(null);
const showMapSelector = ref(false);

// Misc state
const dailyRoutines = ref([]);
const userSettings = ref(null);
const competitiveMaps = ref(null);

// Chart plugin
const clickablePointsPlugin = createClickablePointsPlugin(
  (index) => matchHistory.value?.[matchHistory.value.length - 1 - index]?.match_id,
  (matchId) => router.push(`/matches/${matchId}`),
);
Chart.register(...registerables, clickablePointsPlugin);

// --- Data Composable ---
const filters = {
  platform: selectedPlatform,
  matchType: selectedMatchType,
  map: selectedMap,
};
const steamId = computed(() => user.value?.steam_id);

const {
  profileStats, extraStats, weaponStats, profileLoading, profileError, fetchProfile,
  matchHistory, matchHistoryLoading, totalPageCount, currentPage, fetchMatchHistory,
  progressData, fetchRecentProgress,
  mapStats, fetchMapStats,
  recentStats,
  fetchAll,
} = useCoachData(steamId, filters);

// --- Loading & Empty State ---
const loading = computed(() => profileLoading.value && !profileStats.value);
const showEmptyState = computed(() => {
  if (user.value?.access_level < 1) return true;
  if (profileError.value && !profileStats.value) return true;
  if (!profileLoading.value && !profileStats.value?.kills) return true;
  return false;
});

const coachFeedbackEnabled = computed(() => coachFeedbackEnabledFor(user.value));

const showMatches = computed(() => store.state.siteSettings?.find((s) => s.name === 'show_matches')?.enabled);

// True when the profile loaded successfully but the current filter yields 0 matches
const hasNoFilteredMatches = computed(() => {
  if (profileLoading.value) return false;
  return extraStats.value && extraStats.value.match_count === 0;
});

const noMatchesFilterLabel = computed(() => {
  const parts = [];
  if (selectedPlatform.value) parts.push(platformFilterDisplayLabel(selectedPlatform.value));
  if (selectedMatchType.value) parts.push(selectedMatchType.value);
  if (selectedMap.value?.friendly_name) parts.push(selectedMap.value.friendly_name);
  return parts.length ? parts.join(' / ') : t('coach.page.this_filter_combination');
});

// --- Weapon filtering ---
const weaponTypes = computed(() => categorizeWeapons(weaponStats.value));
const availableWeaponTypes = computed(() => getAvailableWeaponTypes(weaponTypes.value));
const availableWeapons = computed(() => getAvailableWeapons(weaponStats.value, weaponTypes.value, selectedWeaponType.value));
const filteredPlayerStats = computed(() => getFilteredPlayerStats(profileStats.value, weaponStats.value, weaponTypes.value, selectedWeaponType.value, selectedWeapon.value));

// --- Progress (Highlights) ---
const orderedProgress = computed(() => {
  if (!progressData.value) return [];

  const improvements = [];
  for (const [key, stat] of Object.entries(progressData.value)) {
    if (stat.recent_value === undefined || stat.previous_value === undefined) continue;

    const improvement = stat.is_ascending
      ? stat.recent_value - stat.previous_value
      : stat.previous_value - stat.recent_value;

    const previousValue = stat.previous_value === 0 ? 1 : stat.previous_value;
    const percentImprovement = (improvement / previousValue) * 100;

    improvements.push({
      title: key,
      value: stat.recent_value,
      improvement: improvement.toFixed(2),
      percentImprovement: percentImprovement.toFixed(2),
      unit_type: stat.unit_type,
      is_ascending: stat.is_ascending,
    });
  }

  return improvements.sort((a, b) => b.percentImprovement - a.percentImprovement);
});

// --- Weaknesses & Strengths ---
const baseOrderedStats = computed(() => {
  if (!profileStats.value) return [];

  const differences = [];
  for (const [key, stat] of Object.entries(profileStats.value)) {
    if (key === 'ct_roles' || key === 't_roles' || key === 'weapon_stats') continue;
    if (!stat.recommended_mods?.length) continue;
    // Compare against the peer average; skipped when there is no peer data
    const comparison = compareStatToAverage(
      stat.players_value,
      stat.rating_range_average_value,
      stat.is_ascending,
    );

    if (!comparison) continue;

    const { difference, percentDifference } = comparison;

    differences.push({
      title: key,
      friendly_title: key,
      value: stat.players_value,
      difference: difference.toFixed(2),
      percentDifference: percentDifference.toFixed(2),
      unit_type: stat.unit_type,
      is_ascending: stat.is_ascending,
      recommended_mods: stat.recommended_mods,
      rating_range_average_value: stat.rating_range_average_value,
    });
  }

  differences.sort((a, b) => a.percentDifference - b.percentDifference);
  return differences;
});

const lowestStats = computed(() => baseOrderedStats.value.filter((s) => s.percentDifference < 0));

// The coach has no peer average for any stat, so an empty weakness list means
// there was nothing to compare against rather than a clean bill of health.
const rankComparisonUnavailable = computed(() => {
  const comparableStats = Object.values(profileStats.value || {}).filter(
    (stat) => stat?.recommended_mods?.length,
  );

  return (
    comparableStats.length > 0
    && comparableStats.every((stat) => !hasRankAverage(stat.rating_range_average_value))
  );
});
const positiveStats = computed(() => baseOrderedStats.value
  .filter((s) => s.percentDifference > 0)
  .sort((a, b) => b.percentDifference - a.percentDifference));

watch(lowestStats, (newStats) => {
  if (newStats.length > 0) selectedWeaknessStat.value = newStats[0];
}, { immediate: true });

// --- Maps ---
const bestMap = computed(() => mapStats.value?.maps?.reduce((best, cur) => (!best || cur.winrate > best.winrate ? cur : best), null));
const worstMap = computed(() => mapStats.value?.maps?.reduce((worst, cur) => (!worst || cur.winrate < worst.winrate ? cur : worst), null));
const statlessMaps = computed(() => {
  if (!mapStats.value?.maps || !competitiveMaps.value) return [];
  const played = new Set(mapStats.value.maps.map((m) => m.map_name));
  return competitiveMaps.value
    .filter((m) => !played.has(m.name))
    .map((m) => ({ name: m.name, friendly_name: m.friendly_name }));
});

// --- Chart ---
const chartOptions = computed(() => ({
  spanGaps: true,
  plugins: { legend: { display: false }, title: { display: false }, clickablePoints: clickablePointsPlugin },
  elements: { point: { radius: 4, hoverRadius: 6 } },
  scales: {
    x: { display: false, grid: { color: 'transparent', borderColor: 'transparent' } },
    y: {
      grid: { borderDash: [5, 5], color: '#37485D', borderColor: 'transparent' },
      ticks: { color: '#9CADC4', maxTicksLimit: 5 },
      beginAtZero: true,
      suggestedMin: 0,
    },
  },
  maintainAspectRatio: false,
  responsive: true,
}));

// No qualifying events in a match (e.g. no counterstrafing shots) is a 0% rate. Other stats keep
// the gap: a missing time-to-kill plotted as 0 would look like the best match.
function chartValue(match) {
  const { title, unit_type } = selectedWeaknessStat.value;
  return match[title] ?? (unit_type === 'percentage' ? 0 : null);
}

function getChartData() {
  if (!matchHistory.value?.length || !selectedWeaknessStat.value) return null;

  const reversed = [...matchHistory.value].reverse();
  const labels = reversed.map((m) => (m.date || m.played_on || '').split('T')[0]);
  const data = reversed.map((m) => formatData(chartValue(m), selectedWeaknessStat.value.unit_type));

  return {
    labels,
    datasets: [
      {
        data,
        fill: true,
        backgroundColor: '#6B70D115',
        borderColor: '#6B70D1',
        tension: 0,
        pointRadius: 3,
        pointHoverBackgroundColor: '#6B70D1',
        pointHoverBorderColor: '#FFFFFF',
      },
      {
        data: Array(matchHistory.value.length).fill(
          formatData(selectedWeaknessStat.value.rating_range_average_value, selectedWeaknessStat.value.unit_type),
        ),
        fill: false,
        borderColor: '#C44CB6',
        borderDash: [5, 5],
        pointRadius: 0,
        borderWidth: 1,
      },
    ],
  };
}

// --- Filter handlers ---
function onPlatformChange(value) {
  if (selectedPlatform.value !== value) {
    selectedMatchType.value = '';
    selectedMap.value = null;
  }
  selectedPlatform.value = value;
  currentPage.value = 1;
  fetchAll();
}

function onMatchTypeChange(value) {
  selectedMatchType.value = value;
  selectedMap.value = null;
  currentPage.value = 1;
  fetchAll();
}

function onMapChange(value) {
  selectedMap.value = value;
  currentPage.value = 1;
  fetchAll();
}

function onPageChange(page) {
  fetchMatchHistory(page);
}

// --- Side data loaders ---
function getDailyRoutines() {
  axios.get('/task_lists/my_daily_routine')
    .then((res) => { dailyRoutines.value = res.data; })
    .catch(() => {});
}

function getUserSettings() {
  axios.get('/users/my_settings')
    .then((res) => { userSettings.value = res.data; })
    .catch(() => {});
}

// --- Server launch ---
function launchMod(mod) {
  showMapSelector.value = true;
  serverInfo.value = { mode: mod };
  showServerModal.value = true;
}

function launchRoutine(routine) {
  serverInfo.value = {
    mode: 'Creator Studio',
    content: {
      task_list_id: routine.id,
      is_exam: false,
      title: routine.name,
      type: 'task',
      use_difficulty: 1,
    },
  };
  showServerModal.value = true;
}

// --- Map data: derive filter options from the player's actual map stats ---
const playerMaps = computed(() => {
  if (!mapStats.value?.maps?.length) return [];
  return mapStats.value.maps.map((m) => ({
    name: m.map_name,
    friendly_name: m.map_name_friendly || m.map_name,
  }));
});

// Fallback to store's competitive maps for the statlessMaps table
watch(() => store.getters.modInfo, (info) => {
  if (info?.mods) {
    competitiveMaps.value = info.mods.find((m) => m.name === 'competitive')?.cs_maps;
  }
}, { immediate: true });

// --- Init ---
onMounted(() => {
  getUserSettings();
  getDailyRoutines();
  fetchAll();
});

// Every clickablePoints instance shares the plugin id, so leaving this one in
// Chart's global registry would apply this page's row lookup to every chart.
onUnmounted(() => {
  Chart.unregister(clickablePointsPlugin);
});
</script>
